/**
 * The child's own page (mockup 17, [D-157](../../../docs/DECISIONS.md#d-157)).
 *
 * Who they are and what they have done, the sounds they are working on as
 * friendly example words, and their say in how hard lessons are. Nothing here
 * is a score to be anxious about: the sounds are *"practising"*, never
 * *"weak"*, and the difficulty is three plain buttons, the choice winning over
 * the engine until the child changes it
 * ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md#learner-override)).
 *
 * Every part is asked for on its own and fails on its own: a server a deploy
 * behind leaves a card out, never the page ([D-066](../../../docs/DECISIONS.md#d-066)).
 */

import { useEffect, useState } from 'react';
import Animal from './Animal.js';
import AnimalPicker from './AnimalPicker.js';
import type { Client } from './api.js';
import { say } from './audio.js';
import Chest, { type Waiting } from './Chest.js';
import { localDay } from './Quests.js';
import Titles from './Titles.js';

interface Look {
  readonly species: string;
  readonly background: string;
  readonly worn: Parameters<typeof Animal>[0]['worn'];
}
interface Totals {
  readonly xp: number;
  readonly acorns: number;
  readonly streak: number;
}
interface Quest {
  readonly says: string;
  readonly done: number;
  readonly target: number;
  readonly complete: boolean;
}
interface Sound {
  readonly sound: string;
  readonly word: string;
  readonly emoji: string;
  readonly audioRef: string;
}
interface Difficulty {
  readonly band: number;
  readonly name: string;
  readonly chosen: boolean;
}

type Choice = 'easier' | 'just-right' | 'harder';

/** A query that may not exist on an older server: its answer, or null. */
const ask = <T,>(run: () => Promise<T>): Promise<T | null> =>
  Promise.resolve()
    .then(run)
    .catch(() => null);

export default function Profile({ client }: { readonly client: Client }) {
  const [name, setName] = useState<string | null>(null);
  const [look, setLook] = useState<Look | null>(null);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [quests, setQuests] = useState<readonly Quest[] | null>(null);
  const [sounds, setSounds] = useState<readonly Sound[]>([]);
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [busy, setBusy] = useState(false);
  /** The animal picker is open (D-158). */
  const [picking, setPicking] = useState(false);
  /** The words under their name, and whether the titles screen is open (D-159). */
  const [title, setTitle] = useState<string | null>(null);
  const [titling, setTitling] = useState(false);
  /**
   * Chests earned and not opened yet. A child who left the end of a chapter
   * without opening theirs finds it here, kept, rather than lost.
   */
  const [chests, setChests] = useState<readonly Waiting[]>([]);

  useEffect(() => {
    let live = true;
    void ask(() => client.learner.me.query()).then((me) => {
      if (live && me !== null && 'displayName' in me) setName(me.displayName);
    });
    void ask(() => client.shop.loadout.query()).then((loadout) => {
      if (live && loadout !== null) {
        setLook({
          species: loadout.species ?? 'fox',
          background: loadout.background ?? '#eaf2ff',
          worn: loadout.worn ?? {},
        });
      }
    });
    void ask(() => client.shop.titles.query()).then((shelf) => {
      if (live && shelf !== null) {
        setTitle(shelf.titles.find((one) => one.id === shelf.wearing)?.text ?? null);
      }
    });
    void ask(() => client.chest.waiting.query()).then((waiting) => {
      if (live && Array.isArray(waiting)) setChests(waiting);
    });
    void ask(() => client.learner.wallet.query()).then((wallet) => {
      if (live && wallet !== null) setTotals(wallet);
    });
    void ask(() => client.quest.today.query({ day: localDay() })).then((day) => {
      if (live && day !== null) setQuests(day.quests);
    });
    void ask(() => client.learner.practisingSounds.query()).then((found) => {
      if (live && Array.isArray(found)) setSounds(found);
    });
    void ask(() => client.learner.difficulty.query()).then((found) => {
      if (live && found !== null) setDifficulty(found);
    });
    return () => {
      live = false;
    };
  }, [client]);

  const choose = (choice: Choice) => () => {
    if (busy) return;
    setBusy(true);
    void ask(() => client.learner.chooseDifficulty.mutate({ choice })).then((next) => {
      if (next !== null) setDifficulty(next);
      setBusy(false);
    });
  };

  const done = quests?.filter((q) => q.complete).length ?? 0;

  if (titling) {
    return (
      <Titles
        client={client}
        onDone={(wearing) => {
          setTitle(wearing);
          setTitling(false);
        }}
      />
    );
  }

  if (picking && look !== null) {
    return (
      <AnimalPicker
        client={client}
        look={look}
        onDone={(saved) => {
          if (saved !== null) setLook(saved);
          setPicking(false);
        }}
      />
    );
  }

  return (
    <div className="grow pad" style={{ paddingTop: 16 }}>
      <div className="row" style={{ gap: 12 }}>
        {look !== null && (
          <Animal
            species={look.species}
            background={look.background}
            worn={look.worn}
            width={56}
            uid="profile"
          />
        )}
        <div style={{ flex: 1 }}>
          <h2>{name ?? 'Me'}</h2>
          {/* Under the name, where ECONOMY.md puts it (D-159). */}
          {title !== null && (
            <span className="title-badge" style={{ marginTop: 4 }}>
              {title}
            </span>
          )}
        </div>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        {look !== null && (
          <button
            type="button"
            className="chip"
            style={{ minHeight: 48 }}
            onClick={() => {
              setPicking(true);
            }}
          >
            <span aria-hidden="true">🐾</span> Change animal
          </button>
        )}
        <button
          type="button"
          className="chip"
          style={{ minHeight: 48 }}
          onClick={() => {
            setTitling(true);
          }}
        >
          <span aria-hidden="true">🏅</span> Titles
        </button>
      </div>

      {totals !== null && (
        <div className="row" style={{ gap: 9, marginTop: 14 }}>
          <div className="stat">
            <b style={{ color: '#E8843C' }}>{totals.streak}</b>
            <span className="tiny">
              <span aria-hidden="true">🔥</span> day streak
            </span>
          </div>
          <div className="stat">
            <b style={{ color: 'var(--gold-ink)' }}>
              {totals.xp.toLocaleString('en-GB')}
            </b>
            <span className="tiny">
              <span aria-hidden="true">⭐</span> points
            </span>
          </div>
          <div className="stat">
            <b style={{ color: 'var(--acorn-ink)' }}>
              {totals.acorns.toLocaleString('en-GB')}
            </b>
            <span className="tiny">
              <span aria-hidden="true">🌰</span> acorns
            </span>
          </div>
        </div>
      )}

      {/*
        Above the quests, because a chest is the most exciting thing on this
        page. One at a time, like the end of a lesson: two chests at once is two
        celebrations competing, and the second is still here after the first.
      */}
      {chests[0] !== undefined && (
        <section
          className="card"
          style={{ marginTop: 12 }}
          aria-label="Chests waiting for you"
        >
          <h3>
            <span aria-hidden="true">🎁</span>{' '}
            {chests.length === 1
              ? 'A chest is waiting for you!'
              : `${String(chests.length)} chests are waiting for you!`}
          </h3>
          <Chest
            key={chests[0].id}
            chest={chests[0]}
            client={client}
            onDone={() => {
              setChests((was) => was.slice(1));
            }}
          />
        </section>
      )}

      {quests !== null && quests.length > 0 && (
        <section className="card" style={{ marginTop: 12 }} aria-label="Today's quests">
          <div className="between">
            <h3>Today’s quests</h3>
            <span className="tiny" style={{ fontWeight: 800 }}>
              {done}/{quests.length}
            </span>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {quests.map((quest, at) => (
              <li
                key={quest.says}
                className="lrow"
                style={at === quests.length - 1 ? { borderBottom: 0 } : undefined}
              >
                <span className="ic" aria-hidden="true">
                  {quest.complete ? '✅' : '⬜'}
                </span>
                <span className="tx tiny">{quest.says}</span>
                <span className="tiny" style={{ fontWeight: 800 }}>
                  {Math.min(quest.done, quest.target)}/{quest.target}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Only when there is something: "no sounds" is not a thing a child needs
        to be told, and a card of nothing reads as a test they have not taken.
      */}
      {sounds.length > 0 && (
        <section
          className="card"
          style={{ marginTop: 11 }}
          aria-label="Sounds I'm practising"
        >
          <h3>Sounds I’m practising</h3>
          <div
            className="tiles"
            style={{ justifyContent: 'flex-start', marginTop: 10 }}
          >
            {sounds.map((sound) => (
              <button
                key={sound.word}
                type="button"
                className="chip"
                aria-label={`${sound.sound} as in ${sound.word}`}
                onClick={() => {
                  void say(sound.audioRef, sound.word);
                }}
              >
                <span aria-hidden="true">{sound.emoji}</span> {sound.word}
              </button>
            ))}
          </div>
        </section>
      )}

      {difficulty !== null && (
        <section className="card" style={{ marginTop: 11 }} aria-label="How hard?">
          <div className="between">
            <h3>How hard?</h3>
            <span className="chip" role="status">
              {difficulty.name}
              {difficulty.chosen ? ' · your choice' : ''}
            </span>
          </div>
          <p className="tiny" style={{ marginTop: 6 }}>
            Too easy or too hard? You decide. “Just right” lets me choose again.
          </p>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            {(
              [
                ['easier', 'Easier'],
                ['just-right', 'Just right'],
                ['harder', 'Harder'],
              ] as const
            ).map(([choice, label]) => (
              <button
                key={choice}
                type="button"
                className={
                  choice === 'just-right' && !difficulty.chosen
                    ? 'btn navy'
                    : 'btn ghost'
                }
                style={{ fontSize: 13, padding: 11 }}
                disabled={busy}
                onClick={choose(choice)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
